import { round } from "lodash";
import {
  commaToDotTransform,
  dotToCommaTransform,
  removeTrailingCommaOrDot,
} from "@/lib/formatters/NumericTextFieldInputCleaner";
import { formatThousandsWithOptionalDecimals } from "@/lib/formatters/numbers";

export const formatAmountWithCurrency = (
  value: number,
  currencyCode: string | null | undefined
): string =>
  `${formatThousandsWithOptionalDecimals(value)} ${currencyCode ?? ""}`.trim();

export const parseAmount = (value: string): number =>
  parseFloat(commaToDotTransform(value));

/**
 * Reads a settled amount back with a thousands separator, so 125000,5 shows
 * as 125 000,50. It is only for reading: a field holds the plain number
 * while it is being edited, since NumericTextInput turns down a value with
 * a space in it. Input that is not a number yet is left as it is.
 */
export const formatAmountWithThousands = (value: string): string => {
  const amount = parseAmount(value);
  return Number.isNaN(amount)
    ? value
    : formatThousandsWithOptionalDecimals(amount);
};

export const formatAmount = (value: number): string => {
  const rounded = round(value, 2);
  return dotToCommaTransform(
    Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(2)
  );
};

/**
 * Settles what an amount field holds once it loses focus, so that a typed
 * 12,5 reads back as 12,50. In-progress input that is not a number yet is
 * left as it is.
 */
export const normalizeAmountInput = (value: string): string => {
  const trimmed = removeTrailingCommaOrDot(value);
  const amount = parseAmount(trimmed);
  return Number.isNaN(amount) ? trimmed : formatAmount(amount);
};

export const clampToMax = (value: string, max: number): string => {
  const num = parseAmount(value);
  if (Number.isNaN(num)) return value;
  if (num > max) return formatAmount(max);
  return value;
};

export const subtractAmount = (
  amount: string,
  reserved: number,
  previous: string
): string => {
  const result = reserved - parseAmount(amount);
  if (Number.isNaN(result)) return previous;
  return formatAmount(Math.max(0, result));
};

export const isValidAllocation = (
  pay: string,
  release: string,
  reserved: number
): boolean => {
  const payAmount = parseAmount(pay);
  const releaseAmount = parseAmount(release);
  if (Number.isNaN(payAmount) || Number.isNaN(releaseAmount)) return false;
  return round(payAmount + releaseAmount, 2) === reserved;
};
